/**
 * timeframe — convert a TradingView-style resolution string into chart minutes.
 *
 * The Charts page consumes an `?interval=<minutes>` query param (intervals are
 * stored in minutes: 1m=1, 1h=60, 1d=1440, 1w=10080). Alerts carry a
 * `parsedData.timeframe` in TradingView resolution form ('1','5','15','60',
 * '240','D','W' and occasionally '1h'/'4h'/'15m'). This maps those to minutes
 * so a chart opened from an alert lands on the same interval the alert fired on.
 *
 * Returns null when the timeframe is unknown/unmappable (caller then opens the
 * chart on the user's current/last interval).
 */
export function tfToMinutes(tf?: string | number | null): number | null {
  if (tf == null) return null;
  const s = String(tf).trim().toUpperCase();
  if (!s) return null;

  // Bare number = minutes already (TradingView intraday: '1','5','15','60','240').
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return n > 0 ? n : null;
  }

  // Suffixed forms: 15M / 15MIN, 1H / 4H, 1D, 1W.
  const m = s.match(/^(\d+)\s*(MIN|M|H|D|W)$/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (!(n > 0)) return null;
    switch (m[2]) {
      case 'MIN':
      case 'M': return n;            // intraday minutes (e.g. 15m)
      case 'H': return n * 60;
      case 'D': return n * 1440;
      case 'W': return n * 10080;
    }
  }

  // Bare letters.
  if (s === 'D') return 1440;
  if (s === 'W') return 10080;
  return null;
}

/** Build the openChartTab extraQuery for a known timeframe (or undefined). */
export function intervalQueryFor(tf?: string | number | null): Record<string, string> | undefined {
  const mins = tfToMinutes(tf);
  return mins ? { interval: String(mins) } : undefined;
}

/**
 * Build the openChartTab extraQuery from a timeframe AND a strategy, for chart
 * icons clicked from a strategy-aware context (an alert, a trade, an auto-exec
 * row). `strategy` may be a numeric backend strategy id OR a strategy name
 * ('RockBand', 'Sniper', 'DailyBlast', 'Shaved Doji', …) — ChartsPage resolves
 * either into the right overlay. Omits any key it can't derive.
 */
export function chartOpenQuery(
  tf?: string | number | null,
  strategy?: string | number | null,
): Record<string, string> | undefined {
  const q: Record<string, string> = {};
  const mins = tfToMinutes(tf);
  if (mins) q.interval = String(mins);
  if (strategy != null && String(strategy).trim()) q.strategy = String(strategy).trim();
  return Object.keys(q).length ? q : undefined;
}
